import React from 'react'

type Measured = {
    width: number
    props: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- one ref, two runtimes: a DOM node on web, a View instance on native.
        ref: React.RefCallback<any>
        onLayout(event: { nativeEvent: { layout: { width: number } } }): void
    }
}

/**
 * Border-box element width, measured on both platforms. `onLayout` covers native but never
 * fires for the plain DOM nodes NativeWind renders on web, so the web side reads
 * the node directly and follows it with a `ResizeObserver` — without it the
 * width stays 0 in a browser and every width-driven layout silently falls back
 * to its default.
 *
 * The ref is a callback ref, so a node that mounts later or replaces the first
 * one (a body swapped for a no-data state and back) is observed in its place.
 */
export const useMeasuredWidth = (): Measured => {
    const [width, setWidth] = React.useState(0)
    const observer = React.useRef<ResizeObserver | null>(null)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see Measured.props.ref
    const ref = React.useCallback((node: any) => {
        observer.current?.disconnect()
        observer.current = null
        if (typeof ResizeObserver === 'undefined' || !(node instanceof Element)) {
            return
        }
        setWidth(node.getBoundingClientRect().width)
        const next = new ResizeObserver(entries => {
            // `contentRect` is the CONTENT box; native's `onLayout` and the seed
            // above are both border-box, so reading it would silently shrink the
            // width by padding + border on web only.
            const entry = entries[0]
            const measured =
                entry?.borderBoxSize?.[0]?.inlineSize ?? node.getBoundingClientRect().width
            if (measured) {
                setWidth(measured)
            }
        })
        next.observe(node)
        observer.current = next
    }, [])

    const onLayout = React.useCallback(
        (event: { nativeEvent: { layout: { width: number } } }) =>
            setWidth(event.nativeEvent.layout.width),
        [],
    )

    return { width, props: { ref, onLayout } }
}
